// ---------------------------------------------------------------------------
// PWA_ControlBillet — Sound & vibration feedback helpers
// ---------------------------------------------------------------------------

import { useConfigStore } from '@/store/config-store';

// Keep a small cache so we don't re-create Audio objects on every scan.
const audioCache = new Map<string, HTMLAudioElement>();

function getAudio(src: string): HTMLAudioElement {
  let audio = audioCache.get(src);
  if (!audio) {
    audio = new Audio(src);
    audioCache.set(src, audio);
  }
  return audio;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Play a sound file from /sounds/{type}.mp3, respecting the user setting. */
export function playSound(type: 'success' | 'error' | 'warning'): void {
  const { soundEnabled } = useConfigStore.getState();
  if (!soundEnabled) return;

  try {
    const audio = getAudio(`/sounds/${type}.mp3`);
    audio.currentTime = 0;
    audio.play().catch(() => {
      // Autoplay may be blocked — silently ignore.
    });
  } catch {
    // Audio API unavailable — ignore.
  }
}

/** Trigger a vibration pattern, respecting the user setting. */
export function vibrate(pattern: number | number[]): void {
  const { vibrationEnabled } = useConfigStore.getState();
  if (!vibrationEnabled) return;

  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Vibration API unavailable — ignore.
  }
}

/** Success feedback: sound + double vibration pulse. */
export function feedbackSuccess(): void {
  playSound('success');
  vibrate([200, 100, 200]);
}

/** Error feedback: sound + rapid triple vibration. */
export function feedbackError(): void {
  playSound('error');
  vibrate([100, 50, 100, 50, 100]);
}

/** Duplicate feedback: warning sound + single vibration. */
export function feedbackDuplicate(): void {
  playSound('warning');
  vibrate([200]);
}
